import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Target, Heart, Eye, Users, Award, Clock, ArrowRight } from 'lucide-react';
import { GlassCard } from '../components/ui/GlassCard';

const AboutPage = () => {
    const values = [
        { 
            icon: Heart, 
            title: 'Integrity', 
            desc: 'We operate with complete transparency and honesty in all our dealings.'
        },
        { 
            icon: Target, 
            title: 'Excellence', 
            desc: 'We strive for the highest standards in everything we do.'
        },
        { 
            icon: Users, 
            title: 'Partnership', 
            desc: 'We build lasting relationships based on trust and mutual success.'
        },
        { 
            icon: Eye, 
            title: 'Innovation', 
            desc: 'We embrace modern solutions to deliver better results.'
        },
    ];

    const stats = [
        { number: '500+', label: 'Happy Clients' },
        { number: '10+', label: 'Years Experience' },
        { number: '98%', label: 'Client Retention' },
        { number: '24h', label: 'Response Time' },
    ];

    return (
        <main data-testid="about-page">
            {/* Hero Section */}
            <section className="relative pt-32 pb-20 bg-hero-dark overflow-hidden">
                <div className="absolute inset-0 noise-texture" />
                <div className="absolute top-0 right-0 w-96 h-96 bg-hope-soft/20 rounded-full blur-3xl" />
                
                <div className="container-hope relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="max-w-3xl"
                    >
                        <span className="inline-block px-4 py-2 rounded-full bg-white/10 text-white/90 font-medium text-sm mb-6">
                            About Us
                        </span>
                        <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
                            Your Trusted Financial Partners
                        </h1>
                        <p className="text-xl text-white/70 leading-relaxed">
                            For over a decade, Hope Accountancy has been helping UK businesses navigate their financial journey with confidence and clarity.
                        </p>
                    </motion.div>
                </div>
                
                <div className="absolute bottom-0 left-0 right-0">
                    <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#F1F8F4"/>
                    </svg>
                </div>
            </section>

            {/* Story Section */}
            <section className="section-padding bg-hope-light">
                <div className="container-hope">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -40 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                        >
                            <div className="relative">
                                <img 
                                    src="https://images.unsplash.com/photo-1631248055158-edec7a3c072b?crop=entropy&cs=srgb&fm=jpg&q=85"
                                    alt="Our office"
                                    className="rounded-3xl shadow-floating w-full"
                                />
                                <GlassCard className="absolute -bottom-8 -right-8 bg-white/95">
                                    <div className="flex items-center gap-4">
                                        <div className="w-14 h-14 rounded-2xl bg-hope-emerald flex items-center justify-center">
                                            <Award className="w-7 h-7 text-white" />
                                        </div>
                                        <div>
                                            <p className="font-heading font-bold text-2xl text-hope-forest">10+</p>
                                            <p className="text-gray-500 text-sm">Years of Excellence</p>
                                        </div>
                                    </div>
                                </GlassCard>
                            </div>
                        </motion.div>
                        
                        <motion.div
                            initial={{ opacity: 0, x: 40 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                        >
                            <span className="inline-block px-4 py-2 rounded-full bg-hope-emerald/10 text-hope-emerald font-medium text-sm mb-4">
                                Our Story
                            </span>
                            <h2 className="font-heading font-bold text-4xl md:text-5xl text-hope-forest mb-6">
                                Building Trust Through Excellence
                            </h2>
                            <p className="text-gray-600 text-lg leading-relaxed mb-6">
                                Hope Accountancy was founded with a simple mission: to provide premium, personalized accounting services that help businesses thrive. What started as a small practice has grown into a trusted partner for over 500 UK businesses.
                            </p>
                            <p className="text-gray-600 text-lg leading-relaxed mb-8">
                                We believe that every business deserves access to expert financial guidance. Our team combines deep expertise with genuine care for our clients' success, delivering solutions that make a real difference.
                            </p>
                            <Link
                                to="/contact"
                                data-testid="about-cta-btn"
                                className="btn-premium btn-primary inline-flex items-center gap-3"
                            >
                                Get in Touch <ArrowRight className="w-5 h-5" />
                            </Link>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-20 bg-white">
                <div className="container-hope">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                        {stats.map((stat, index) => (
                            <motion.div
                                key={stat.label}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="text-center"
                            >
                                <p className="font-heading font-bold text-5xl md:text-6xl text-hope-emerald mb-2">
                                    {stat.number}
                                </p>
                                <p className="text-gray-600 font-medium">{stat.label}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Mission & Values */}
            <section className="section-padding bg-hope-light">
                <div className="container-hope">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <span className="inline-block px-4 py-2 rounded-full bg-hope-emerald/10 text-hope-emerald font-medium text-sm mb-4">
                            Our Values
                        </span>
                        <h2 className="font-heading font-bold text-4xl md:text-5xl text-hope-forest mb-4">
                            What Drives Us
                        </h2>
                        <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                            Our core values guide everything we do and shape the service we deliver
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {values.map((value, index) => (
                            <motion.div
                                key={value.title}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                data-testid={`value-card-${index}`}
                            >
                                <GlassCard className="h-full text-center">
                                    <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-hope-emerald flex items-center justify-center">
                                        <value.icon className="w-8 h-8 text-white" />
                                    </div>
                                    <h3 className="font-heading font-semibold text-xl text-hope-forest mb-3">
                                        {value.title}
                                    </h3>
                                    <p className="text-gray-600">{value.desc}</p>
                                </GlassCard>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="section-padding bg-hero-dark relative overflow-hidden">
                <div className="absolute inset-0 noise-texture" />
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-hope-soft/20 rounded-full blur-3xl" />
                
                <div className="container-hope relative z-10 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="font-heading font-bold text-4xl md:text-5xl text-white mb-6">
                            Ready to Work Together?
                        </h2>
                        <p className="text-white/70 text-xl mb-10 max-w-2xl mx-auto">
                            Let's discuss how we can support your business growth
                        </p>
                        <Link
                            to="/contact"
                            data-testid="about-bottom-cta"
                            className="btn-premium bg-white text-hope-emerald hover:bg-hope-light inline-flex items-center gap-3"
                        >
                            Book Free Consultation
                            <ArrowRight className="w-5 h-5" />
                        </Link>
                    </motion.div>
                </div>
            </section>
        </main>
    );
};

export default AboutPage;
